/**
 * Sysadmin Platform → Helper Budgets (EP-2 §9.1).
 *
 * Matrix editor: rows = brands, columns = helper kinds. Each cell shows
 * the resolved (first_token / inactivity / total) triple plus a chip
 * indicating the source layer (explicit / inherited / factory).
 *
 * Clicking a cell opens an inline editor with:
 *   - the 4 numeric inputs
 *   - EP-1 SLO baseline (if available) for context
 *   - notes
 *   - save / delete / reset-to-inherited buttons
 *
 * The header "Seed from SLO baselines" CTA opens a confirmation dialog
 * that first calls the seed endpoint with dryRun=true and shows the
 * proposed diff; the operator then confirms to apply.
 *
 * Per EP-2 spec §1 — this UI is the operator's primary lever for fixing
 * the architect-on-slow-hardware case (and any future timeouts caused
 * by a too-tight budget for a slow brand on a slow box).
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Timer,
  Sparkles,
  AlertTriangle,
  RefreshCw,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { SectionCard } from '@/components/ui/section-card';
import { PageError } from '@/components/ui/page-error';
import { StatTile } from '@/components/ui/stat-tile';
import {
  useHelperBudgetMatrix,
  useCreateHelperBudget,
  useUpdateHelperBudget,
  useDeleteHelperBudget,
  useSeedFromSloBaselines,
  type MatrixCell,
  type ResolutionSource,
} from '@/lib/use-sysadmin-ep2';

export const Route = createFileRoute('/sysadmin/platform/helper-budgets')({
  component: HelperBudgetsPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(0)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

const SOURCE_LABEL: Record<ResolutionSource, string> = {
  db_explicit: 'override',
  db_inherited_brand: 'brand default',
  db_inherited_kind: 'kind default',
  db_factory: 'factory',
};

const SOURCE_TONE: Record<ResolutionSource, 'override' | 'inherited' | 'factory'> = {
  db_explicit: 'override',
  db_inherited_brand: 'inherited',
  db_inherited_kind: 'inherited',
  db_factory: 'factory',
};

function chipClass(source: ResolutionSource): string {
  const tone = SOURCE_TONE[source];
  if (tone === 'override') return 'bg-[#1890FF]/10 text-[#1890FF] border-[#1890FF]/30';
  if (tone === 'inherited') return 'bg-muted text-muted-foreground';
  return 'bg-amber-500/10 text-amber-700 border-amber-500/30';
}

// ─── Cell editor dialog ───────────────────────────────────────────────────

interface CellEditorProps {
  open: boolean;
  onClose: () => void;
  cell: MatrixCell | null;
}

function CellEditor({ open, onClose, cell }: CellEditorProps) {
  const create = useCreateHelperBudget();
  // We need the row id to know which mutation to invoke. For inherited
  // cells the sourceRowId is the inherited row (which we don't want to
  // mutate — we want to CREATE a new explicit row). The mutation pick
  // is decided at submit time.
  const updateMutation = useUpdateHelperBudget(cell?.sourceRowId ?? '');
  const deleteMutation = useDeleteHelperBudget(cell?.sourceRowId ?? '');

  const [firstToken, setFirstToken] = useState(0);
  const [inactivity, setInactivity] = useState(0);
  const [total, setTotal] = useState(0);
  const [heartbeat, setHeartbeat] = useState(5000);
  const [notes, setNotes] = useState('');

  // Reset form when cell changes.
  if (cell && firstToken === 0) {
    setFirstToken(cell.resolved.firstTokenBudgetMs);
    setInactivity(cell.resolved.streamInactivityBudgetMs);
    setTotal(cell.resolved.totalBudgetMs);
    setHeartbeat(cell.resolved.heartbeatIntervalMs);
  }

  if (!cell) return null;

  const isOverride = cell.source === 'db_explicit';
  const invariantErr =
    total < firstToken
      ? `Total (${formatMs(total)}) must be >= first-token (${formatMs(firstToken)})`
      : total < inactivity
      ? `Total (${formatMs(total)}) must be >= inactivity (${formatMs(inactivity)})`
      : null;

  const handleSave = async () => {
    const payload = {
      firstTokenBudgetMs: firstToken,
      streamInactivityBudgetMs: inactivity,
      totalBudgetMs: total,
      heartbeatIntervalMs: heartbeat,
      notes: notes || null,
    };
    if (isOverride) {
      await updateMutation.mutateAsync(payload);
    } else {
      // Create a new explicit override row at the (brand, kind) layer.
      await create.mutateAsync({
        brandId: cell.brandId,
        helperKind: cell.helperKind,
        ...payload,
      });
    }
    onClose();
    // Reset state so next open picks up new cell values.
    setFirstToken(0);
    setInactivity(0);
    setTotal(0);
    setHeartbeat(5000);
    setNotes('');
  };

  const handleResetToInherited = async () => {
    if (!isOverride) return;
    if (!confirm(`Delete this override and revert to inherited values?`)) return;
    await deleteMutation.mutateAsync(undefined);
    onClose();
    setFirstToken(0);
    setInactivity(0);
    setTotal(0);
    setHeartbeat(5000);
    setNotes('');
  };

  const useSuggestedFromBaseline = () => {
    if (!cell.sloBaseline) return;
    const suggested = Math.max(cell.sloBaseline.firstTokenP95Ms * 1.5, 10_000);
    setFirstToken(Math.round(suggested));
    setTotal(Math.round(suggested + (cell.helperKind === 'architect' ? 180_000 : 60_000)));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5 text-[#1890FF]" />
            {cell.brandSlug} / {cell.helperKind}
          </DialogTitle>
          <DialogDescription>
            <Badge variant="outline" className={chipClass(cell.source)}>
              {SOURCE_LABEL[cell.source]}
            </Badge>
            {cell.sloBaseline && (
              <span className="ml-2 text-xs text-muted-foreground">
                EP-1 baseline: p50 {formatMs(cell.sloBaseline.firstTokenP50Ms)}, p95{' '}
                {formatMs(cell.sloBaseline.firstTokenP95Ms)} at{' '}
                {cell.sloBaseline.contextSizeTokens} tokens
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="ft">First-token budget (ms)</Label>
              <Input
                id="ft"
                type="number"
                value={firstToken}
                onChange={(e) => setFirstToken(Number(e.target.value))}
                min={1000}
                max={600_000}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="ia">Stream inactivity (ms)</Label>
              <Input
                id="ia"
                type="number"
                value={inactivity}
                onChange={(e) => setInactivity(Number(e.target.value))}
                min={1000}
                max={600_000}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="tt">Total budget (ms)</Label>
              <Input
                id="tt"
                type="number"
                value={total}
                onChange={(e) => setTotal(Number(e.target.value))}
                min={5000}
                max={1_800_000}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="hb">Heartbeat interval (ms)</Label>
              <Input
                id="hb"
                type="number"
                value={heartbeat}
                onChange={(e) => setHeartbeat(Number(e.target.value))}
                min={1000}
                max={60_000}
              />
            </div>
          </div>

          {cell.sloBaseline && (
            <Button
              variant="outline"
              size="sm"
              onClick={useSuggestedFromBaseline}
              className="gap-2"
            >
              <Sparkles className="h-4 w-4" />
              Use 1.5× p95 from EP-1 baseline (~
              {formatMs(Math.max(cell.sloBaseline.firstTokenP95Ms * 1.5, 10_000))}{' '}
              first-token)
            </Button>
          )}

          <div className="flex flex-col gap-1">
            <Label htmlFor="nt">Notes</Label>
            <Textarea
              id="nt"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Why this override? e.g., customer running argus_pro on Strix Halo"
            />
          </div>

          {invariantErr && (
            <div className="rounded border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {invariantErr}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          {isOverride ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleResetToInherited}
              disabled={deleteMutation.isPending}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Reset to inherited
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                invariantErr !== null ||
                create.isPending ||
                updateMutation.isPending
              }
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {isOverride ? 'Save override' : 'Create override'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Seed-from-baselines dialog ───────────────────────────────────────────

function SeedDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const seed = useSeedFromSloBaselines();
  const [dryRunResult, setDryRunResult] = useState<Awaited<
    ReturnType<typeof seed.mutateAsync>
  > | null>(null);
  const [overwriteOperator, setOverwriteOperator] = useState(false);

  const handleDryRun = async () => {
    const result = await seed.mutateAsync({
      dryRun: true,
      overwriteOperatorRows: overwriteOperator,
    });
    setDryRunResult(result);
  };

  const handleApply = async () => {
    await seed.mutateAsync({
      dryRun: false,
      overwriteOperatorRows: overwriteOperator,
    });
    setDryRunResult(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setDryRunResult(null);
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#1890FF]" />
            Seed budgets from EP-1 SLO baselines
          </DialogTitle>
          <DialogDescription>
            Computes brand-default budgets from each brand's measured p95
            first-token latency × 1.5 + headroom for inactivity. Existing
            operator-authored rows are preserved by default.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={overwriteOperator}
              onChange={(e) => setOverwriteOperator(e.target.checked)}
            />
            Overwrite operator-authored rows (destructive — confirm with care)
          </label>

          {!dryRunResult ? (
            <Button onClick={handleDryRun} disabled={seed.isPending}>
              Preview changes (dry run)
            </Button>
          ) : (
            <div className="flex flex-col gap-3 text-sm">
              <div>
                <strong>Proposed inserts ({dryRunResult.inserts.length}):</strong>
                {dryRunResult.inserts.length === 0 ? (
                  <p className="text-muted-foreground">
                    No changes — every brand already has a brand-default row.
                  </p>
                ) : (
                  <ul className="ml-4 list-disc">
                    {dryRunResult.inserts.map((i) => (
                      <li key={i.brandId}>
                        {i.brandSlug}: first-token {formatMs(i.firstTokenBudgetMs)},
                        inactivity {formatMs(i.streamInactivityBudgetMs)}, total{' '}
                        {formatMs(i.totalBudgetMs)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {dryRunResult.skippedOperatorRows.length > 0 && (
                <div>
                  <strong>Skipped (operator-authored):</strong>
                  <ul className="ml-4 list-disc text-muted-foreground">
                    {dryRunResult.skippedOperatorRows.map((s) => (
                      <li key={s.brandId}>
                        {s.brandSlug} — {s.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {dryRunResult.brandsWithoutBaseline.length > 0 && (
                <div>
                  <strong>Brands without a baseline (unchanged):</strong>
                  <ul className="ml-4 list-disc text-muted-foreground">
                    {dryRunResult.brandsWithoutBaseline.map((b) => (
                      <li key={b.brandId}>{b.brandSlug}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {dryRunResult && dryRunResult.inserts.length > 0 && (
            <Button onClick={handleApply} disabled={seed.isPending}>
              Apply
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page component ───────────────────────────────────────────────────────

function HelperBudgetsPage() {
  const { data, isLoading, error, refetch } = useHelperBudgetMatrix();
  const [selectedCell, setSelectedCell] = useState<MatrixCell | null>(null);
  const [seedOpen, setSeedOpen] = useState(false);

  if (error) {
    return <PageError error={error} onRetry={refetch} />;
  }

  const matrix = data?.matrix ?? [];
  const brands = data?.brands ?? [];
  const helperKinds = data?.helperKinds ?? [];

  // Index matrix by (brandId, kind) for quick cell lookup.
  const cellMap = new Map<string, MatrixCell>();
  for (const c of matrix) cellMap.set(`${c.brandId}:${c.helperKind}`, c);

  const overrideCount = matrix.filter((c) => c.source === 'db_explicit').length;
  const inheritedCount = matrix.filter(
    (c) => c.source === 'db_inherited_brand' || c.source === 'db_inherited_kind',
  ).length;
  const factoryCount = matrix.filter((c) => c.source === 'db_factory').length;

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Timer className="h-6 w-6 text-[#1890FF]" />
            Helper Budgets
          </h1>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Per-(brand, helper kind) streaming budgets. Each helper-chat
            request resolves its first-token, stream-inactivity, total, and
            heartbeat values from this matrix. Click any cell to override.{' '}
            <span className="text-foreground font-medium">EP-2</span>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()} className="gap-2">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          <Button onClick={() => setSeedOpen(true)} className="gap-2">
            <Sparkles className="h-4 w-4" /> Seed from SLO baselines
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatTile label="Brands" value={brands.length} icon={Timer} />
        <StatTile label="Operator overrides" value={overrideCount} />
        <StatTile label="Inherited cells" value={inheritedCount} />
        <StatTile
          label="Factory default cells"
          value={factoryCount}
          tone={factoryCount === matrix.length && matrix.length > 0 ? 'warning' : undefined}
        />
      </div>

      <SectionCard
        title="Budget matrix"
        description={
          isLoading
            ? 'Loading…'
            : `${brands.length} brand(s) × ${helperKinds.length} helper kind(s) = ${matrix.length} cells`
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                <th className="p-2">Brand</th>
                {helperKinds.map((k) => (
                  <th key={k} className="p-2 text-center">
                    {k}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {brands.map((brand) => (
                <tr key={brand.id} className="border-b">
                  <td className="p-2 font-medium">{brand.slug}</td>
                  {helperKinds.map((kind) => {
                    const cell = cellMap.get(`${brand.id}:${kind}`);
                    if (!cell) {
                      return (
                        <td key={kind} className="p-2 text-center text-xs text-muted-foreground">
                          —
                        </td>
                      );
                    }
                    return (
                      <td key={kind} className="p-2">
                        <button
                          onClick={() => setSelectedCell(cell)}
                          className="w-full rounded border border-border bg-card px-2 py-1.5 text-left hover:border-[#1890FF]/40 hover:bg-[#1890FF]/5 transition"
                        >
                          <div className="text-xs font-medium">
                            {formatMs(cell.resolved.firstTokenBudgetMs)} ·{' '}
                            {formatMs(cell.resolved.streamInactivityBudgetMs)} ·{' '}
                            {formatMs(cell.resolved.totalBudgetMs)}
                          </div>
                          <Badge variant="outline" className={`mt-1 text-[10px] ${chipClass(cell.source)}`}>
                            {SOURCE_LABEL[cell.source]}
                          </Badge>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {brands.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={helperKinds.length + 1} className="p-4 text-center text-muted-foreground">
                    No brands registered.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-xs text-muted-foreground">
          Cell format: first-token · stream-inactivity · total. Changes
          propagate to all API replicas within 30 seconds.
        </div>
      </SectionCard>

      <CellEditor
        open={selectedCell !== null}
        cell={selectedCell}
        onClose={() => setSelectedCell(null)}
      />
      <SeedDialog open={seedOpen} onClose={() => setSeedOpen(false)} />
    </div>
  );
}
